import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useReplicache } from "../replicache/context";
import type { Canvas } from "../replicache/keys";

const SAVE_DEBOUNCE_MS = 600;
const ERASER_WIDTH = 24;
const MIN_SCALE = 0.05;
const MAX_SCALE = 20;

// Points are world-space logical pixels (origin at canvas top-left, independent of pan/zoom).
type Point = [number, number];
type Stroke = { c: string; w: number; p: Point[] };

const COLORS = ["#1e293b", "#ef4444", "#f97316", "#3b82f6", "#22c55e", "#a855f7"];
const COLOR_LABELS = ["Slate", "Red", "Orange", "Blue", "Green", "Purple"];
const WIDTHS = [2, 4, 8];

// Strokes are stored as { v: 2, strokes: Stroke[] }.
// v1 (plain array with normalized [0-1] coords) is migrated on read.
function parseStrokes(raw: string): Stroke[] {
  try {
    const val = JSON.parse(raw) as unknown;
    if (!val) return [];
    if (typeof val === "object" && !Array.isArray(val)) {
      const doc = val as { v?: number; strokes?: unknown };
      if (doc.v === 2 && Array.isArray(doc.strokes)) return doc.strokes as Stroke[];
      return [];
    }
    if (Array.isArray(val) && val.length > 0) {
      // v1: normalize to world-space using approximate viewport size
      const w = window.innerWidth * 0.72;
      const h = window.innerHeight - 140;
      return (val as Array<{ c: string; w: number; p: [number, number][] }>).map((s) => ({
        c: s.c,
        w: s.w,
        p: s.p.map(([x, y]) => [x * w, y * h] as Point),
      }));
    }
    return [];
  } catch {
    return [];
  }
}

function serializeStrokes(strokes: Stroke[]): string {
  return JSON.stringify({ v: 2, strokes });
}

function syncCursor(el: HTMLCanvasElement, hand: boolean, eraser: boolean, panning: boolean) {
  el.style.cursor = panning ? "grabbing" : hand ? "grab" : eraser ? "cell" : "crosshair";
}

export function CanvasView({
  canvas,
  onClose,
}: {
  canvas: Canvas;
  onClose: () => void;
}) {
  const rep = useReplicache();
  const [name, setName] = useState(canvas.name);
  const [strokes, setStrokes] = useState<Stroke[]>(() => parseStrokes(canvas.strokes));
  const [color, setColor] = useState(COLORS[0]!);
  const [penWidth, setPenWidth] = useState(WIDTHS[1]!);
  const [eraser, setEraser] = useState(false);
  const [hand, setHand] = useState(false);
  const [scale, setScale] = useState(1);

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const strokesRef = useRef<Stroke[]>(strokes);
  const currentRef = useRef<Stroke | null>(null);
  const colorRef = useRef(color);
  const penWidthRef = useRef(penWidth);
  const eraserRef = useRef(eraser);
  const handRef = useRef(hand);
  const spaceRef = useRef(false);
  // panRef: world-space coords of the viewport's top-left corner
  const panRef = useRef({ x: 0, y: 0 });
  const scaleRef = useRef(1);
  const panStartRef = useRef<{ panX: number; panY: number; startX: number; startY: number } | null>(null);
  // pinchRef: state captured at the start of a two-finger pinch
  const pinchRef = useRef<{
    pan: { x: number; y: number };
    scale: number;
    mid: { x: number; y: number }; // screen coords of midpoint when pinch started
    dist: number;
  } | null>(null);
  // tracks all active pointers by id → client coords
  const activePtrsRef = useRef(new Map<number, { x: number; y: number }>());
  const dirtyRef = useRef(false);
  const canvasIdRef = useRef(canvas.id);
  const nameRef = useRef(name);

  useLayoutEffect(() => { strokesRef.current = strokes; }, [strokes]);
  useEffect(() => { colorRef.current = color; }, [color]);
  useEffect(() => { penWidthRef.current = penWidth; }, [penWidth]);
  useEffect(() => { eraserRef.current = eraser; }, [eraser]);
  useEffect(() => { handRef.current = hand; }, [hand]);

  // Keep cursor in sync with React state (hand/eraser toggles)
  useEffect(() => {
    if (canvasRef.current) syncCursor(canvasRef.current, hand, eraser, false);
  }, [hand, eraser]);

  useEffect(() => { nameRef.current = name; }, [name]);

  // Reset when switching to a different canvas
  useEffect(() => {
    if (canvasIdRef.current !== canvas.id) {
      canvasIdRef.current = canvas.id;
      dirtyRef.current = false;
      panRef.current = { x: 0, y: 0 };
      scaleRef.current = 1;
      setScale(1);
      const parsed = parseStrokes(canvas.strokes);
      strokesRef.current = parsed;
      setStrokes(parsed);
      setName(canvas.name);
      return;
    }
    if (!dirtyRef.current) {
      const parsed = parseStrokes(canvas.strokes);
      strokesRef.current = parsed;
      setStrokes(parsed);
      if (canvas.name !== name) setName(canvas.name);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas]);

  // Debounced save
  useEffect(() => {
    if (!rep || !dirtyRef.current) return;
    const tid = window.setTimeout(() => {
      void (async () => {
        const serialized = serializeStrokes(strokesRef.current);
        const patch: { id: string; name?: string; strokes?: string } = { id: canvas.id };
        if (serialized !== canvas.strokes) patch.strokes = serialized;
        if (nameRef.current !== canvas.name) patch.name = nameRef.current;
        if (patch.name !== undefined || patch.strokes !== undefined) {
          await rep.mutate.updateCanvas(patch);
        }
        dirtyRef.current = false;
      })();
    }, SAVE_DEBOUNCE_MS);
    return () => window.clearTimeout(tid);
  }, [strokes, name, rep, canvas.id, canvas.strokes, canvas.name]);

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (!rep || !dirtyRef.current) return;
      void rep.mutate.updateCanvas({
        id: canvas.id,
        strokes: serializeStrokes(strokesRef.current),
        name: nameRef.current,
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canvas.id]);

  const renderCanvas = useCallback(() => {
    const el = canvasRef.current;
    if (!el) return;
    const ctx = el.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const cw = el.clientWidth;
    const ch = el.clientHeight;
    if (cw === 0 || ch === 0) return;
    const targetW = Math.round(cw * dpr);
    const targetH = Math.round(ch * dpr);
    if (el.width !== targetW || el.height !== targetH) {
      el.width = targetW;
      el.height = targetH;
    }

    const { x: px, y: py } = panRef.current;
    const s = scaleRef.current;
    // Scale by (DPR × zoom), shift by pan offset
    ctx.setTransform(dpr * s, 0, 0, dpr * s, -px * dpr * s, -py * dpr * s);

    ctx.fillStyle = "#ffffff";
    // Fill only the visible rect in world-space coords
    ctx.fillRect(px, py, cw / s, ch / s);

    const all = [...strokesRef.current, ...(currentRef.current ? [currentRef.current] : [])];
    for (const stroke of all) {
      if (stroke.p.length === 0) continue;
      ctx.beginPath();
      ctx.strokeStyle = stroke.c;
      ctx.fillStyle = stroke.c;
      ctx.lineWidth = stroke.w;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      if (stroke.p.length === 1) {
        ctx.arc(stroke.p[0]![0], stroke.p[0]![1], stroke.w / 2, 0, Math.PI * 2);
        ctx.fill();
      } else {
        ctx.moveTo(stroke.p[0]![0], stroke.p[0]![1]);
        for (let i = 1; i < stroke.p.length; i++) {
          ctx.lineTo(stroke.p[i]![0], stroke.p[i]![1]);
        }
        ctx.stroke();
      }
    }
  }, []);

  useLayoutEffect(() => { renderCanvas(); }, [strokes, renderCanvas]);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const obs = new ResizeObserver(() => renderCanvas());
    obs.observe(el);
    return () => obs.disconnect();
  }, [renderCanvas]);

  // Space key → temporary hand/pan mode
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.code !== "Space" || e.repeat) return;
      const tag = (document.activeElement as HTMLElement | null)?.tagName ?? "";
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      e.preventDefault();
      spaceRef.current = true;
      if (canvasRef.current) syncCursor(canvasRef.current, true, eraserRef.current, false);
    };
    const onKeyUp = (e: KeyboardEvent) => {
      if (e.code !== "Space") return;
      spaceRef.current = false;
      if (canvasRef.current) syncCursor(canvasRef.current, handRef.current, eraserRef.current, false);
    };
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("keyup", onKeyUp);
    };
  }, []);

  // Pointer events + wheel
  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;

    // Debounce the scale state update to avoid re-rendering on every wheel tick
    let scaleDisplayTimer: ReturnType<typeof setTimeout> | null = null;
    const scheduleScaleDisplay = (s: number) => {
      if (scaleDisplayTimer !== null) clearTimeout(scaleDisplayTimer);
      scaleDisplayTimer = setTimeout(() => setScale(s), 120);
    };

    // Convert a pointer event's client position to world-space
    const toWorld = (e: PointerEvent): Point => {
      const rect = el.getBoundingClientRect();
      const s = scaleRef.current;
      return [
        (e.clientX - rect.left) / s + panRef.current.x,
        (e.clientY - rect.top) / s + panRef.current.y,
      ];
    };

    const beginPan = (cx: number, cy: number) => {
      panStartRef.current = { panX: panRef.current.x, panY: panRef.current.y, startX: cx, startY: cy };
    };

    const applyPan = (cx: number, cy: number) => {
      if (!panStartRef.current) return;
      panRef.current = {
        x: panStartRef.current.panX - (cx - panStartRef.current.startX),
        y: panStartRef.current.panY - (cy - panStartRef.current.startY),
      };
    };

    const onDown = (e: PointerEvent) => {
      if (e.button !== 0 && e.pointerType === "mouse") return;
      activePtrsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });
      el.setPointerCapture(e.pointerId);

      const twoFinger = e.pointerType === "touch" && activePtrsRef.current.size >= 2;
      if (twoFinger) {
        // Cancel any in-progress stroke or single-finger pan, start pinch
        currentRef.current = null;
        panStartRef.current = null;
        const pts = Array.from(activePtrsRef.current.values());
        const mid = {
          x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
          y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
        };
        const [p0, p1] = pts;
        const dist = Math.hypot(p1!.x - p0!.x, p1!.y - p0!.y);
        pinchRef.current = { pan: { ...panRef.current }, scale: scaleRef.current, mid, dist };
        renderCanvas();
        return;
      }

      if (handRef.current || spaceRef.current) {
        beginPan(e.clientX, e.clientY);
        syncCursor(el, true, eraserRef.current, true);
        return;
      }

      currentRef.current = {
        c: eraserRef.current ? "#ffffff" : colorRef.current,
        w: eraserRef.current ? ERASER_WIDTH : penWidthRef.current,
        p: [toWorld(e)],
      };
      renderCanvas();
    };

    const onMove = (e: PointerEvent) => {
      activePtrsRef.current.set(e.pointerId, { x: e.clientX, y: e.clientY });

      // Two-finger pinch: combined pan + zoom
      if (pinchRef.current && e.pointerType === "touch" && activePtrsRef.current.size >= 2) {
        const pts = Array.from(activePtrsRef.current.values());
        const currentMid = {
          x: pts.reduce((s, p) => s + p.x, 0) / pts.length,
          y: pts.reduce((s, p) => s + p.y, 0) / pts.length,
        };
        const [p0, p1] = pts;
        const currentDist = Math.hypot(p1!.x - p0!.x, p1!.y - p0!.y);

        const { pan: startPan, scale: startScale, mid: startMid, dist: startDist } = pinchRef.current;
        const rect = el.getBoundingClientRect();

        const newScale = Math.min(Math.max(startScale * (currentDist / startDist), MIN_SCALE), MAX_SCALE);
        scaleRef.current = newScale;

        // World point that was under the start midpoint
        const worldX = (startMid.x - rect.left) / startScale + startPan.x;
        const worldY = (startMid.y - rect.top) / startScale + startPan.y;

        // Keep that world point under the current midpoint (pan + zoom together)
        panRef.current = {
          x: worldX - (currentMid.x - rect.left) / newScale,
          y: worldY - (currentMid.y - rect.top) / newScale,
        };

        renderCanvas();
        return;
      }

      if (panStartRef.current) {
        applyPan(e.clientX, e.clientY);
        renderCanvas();
        return;
      }

      if (!currentRef.current) return;
      currentRef.current.p.push(toWorld(e));
      renderCanvas();
    };

    const onUp = (e: PointerEvent) => {
      activePtrsRef.current.delete(e.pointerId);
      const remaining = activePtrsRef.current.size;

      if (pinchRef.current) {
        if (e.pointerType !== "touch" || remaining < 2) {
          pinchRef.current = null;
          setScale(scaleRef.current);
          syncCursor(el, handRef.current, eraserRef.current, false);
        }
        return;
      }

      if (panStartRef.current) {
        const done = e.pointerType === "touch" ? remaining < 2 : remaining === 0;
        if (done) {
          panStartRef.current = null;
          syncCursor(el, handRef.current, eraserRef.current, false);
        }
        return;
      }

      if (!currentRef.current) return;
      const stroke = currentRef.current;
      currentRef.current = null;
      if (stroke.p.length > 0) {
        const next = [...strokesRef.current, stroke];
        strokesRef.current = next;
        dirtyRef.current = true;
        setStrokes(next);
      }
      renderCanvas();
    };

    const onCancel = (e: PointerEvent) => {
      activePtrsRef.current.delete(e.pointerId);
      currentRef.current = null;
      if (activePtrsRef.current.size < 2) {
        panStartRef.current = null;
        pinchRef.current = null;
      }
      syncCursor(el, handRef.current, eraserRef.current, false);
      renderCanvas();
    };

    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      const rect = el.getBoundingClientRect();

      if (e.ctrlKey) {
        // Trackpad pinch gesture or Ctrl+scroll → zoom at cursor position
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        const old = scaleRef.current;
        const factor = Math.exp(-e.deltaY / 300);
        const newScale = Math.min(Math.max(old * factor, MIN_SCALE), MAX_SCALE);
        scaleRef.current = newScale;
        panRef.current = {
          x: panRef.current.x + sx * (1 / old - 1 / newScale),
          y: panRef.current.y + sy * (1 / old - 1 / newScale),
        };
        scheduleScaleDisplay(newScale);
      } else {
        // Scroll/swipe → pan
        panRef.current = { x: panRef.current.x + e.deltaX, y: panRef.current.y + e.deltaY };
      }
      renderCanvas();
    };

    el.addEventListener("pointerdown", onDown);
    el.addEventListener("pointermove", onMove);
    el.addEventListener("pointerup", onUp);
    el.addEventListener("pointercancel", onCancel);
    el.addEventListener("wheel", onWheel, { passive: false });
    return () => {
      el.removeEventListener("pointerdown", onDown);
      el.removeEventListener("pointermove", onMove);
      el.removeEventListener("pointerup", onUp);
      el.removeEventListener("pointercancel", onCancel);
      el.removeEventListener("wheel", onWheel);
      if (scaleDisplayTimer !== null) clearTimeout(scaleDisplayTimer);
    };
  }, [renderCanvas]);

  // Zoom at the canvas center (used by toolbar +/− buttons)
  const zoomAtCenter = useCallback((newScale: number) => {
    const el = canvasRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const sx = rect.width / 2;
    const sy = rect.height / 2;
    const old = scaleRef.current;
    const clamped = Math.min(Math.max(newScale, MIN_SCALE), MAX_SCALE);
    scaleRef.current = clamped;
    panRef.current = {
      x: panRef.current.x + sx * (1 / old - 1 / clamped),
      y: panRef.current.y + sy * (1 / old - 1 / clamped),
    };
    setScale(clamped);
    renderCanvas();
  }, [renderCanvas]);

  const handleUndo = () => {
    const next = strokes.slice(0, -1);
    strokesRef.current = next;
    dirtyRef.current = true;
    setStrokes(next);
  };

  const handleClear = () => {
    if (!confirm("Clear all strokes? This cannot be undone.")) return;
    strokesRef.current = [];
    dirtyRef.current = true;
    setStrokes([]);
  };

  const handleDelete = async () => {
    if (!rep) return;
    if (!confirm(`Delete canvas "${name || "Untitled"}"? This cannot be undone.`)) return;
    await rep.mutate.deleteCanvas({ id: canvas.id });
    onClose();
  };

  const handleResetView = () => {
    panRef.current = { x: 0, y: 0 };
    scaleRef.current = 1;
    setScale(1);
    renderCanvas();
  };

  return (
    <section className="flex flex-1 flex-col overflow-hidden">
      <header className="flex items-center gap-2 border-b border-slate-200 px-4 py-2 sm:px-6 dark:border-slate-700">
        <button
          type="button"
          onClick={onClose}
          title="Back to Canvases"
          aria-label="Back to Canvases"
          className="rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700 dark:text-slate-500 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
          ←
        </button>
        <span aria-hidden className="text-base">🎨</span>
        <input
          value={name}
          onChange={(e) => {
            dirtyRef.current = true;
            setName(e.target.value);
          }}
          placeholder="Untitled canvas"
          className="flex-1 rounded border border-transparent bg-transparent px-1 py-1 text-lg font-semibold tracking-tight focus:border-slate-300 focus:bg-white focus:outline-none dark:focus:border-slate-600 dark:focus:bg-slate-800"
        />
        <span className="hidden text-[11px] text-slate-400 sm:inline dark:text-slate-500">
          Auto-saved · offline-synced
        </span>
        <button
          type="button"
          onClick={() => void handleDelete()}
          className="rounded px-2 py-0.5 text-xs font-medium text-rose-600 hover:bg-rose-50 hover:text-rose-800 dark:text-rose-400 dark:hover:bg-rose-950 dark:hover:text-rose-300"
        >
          Delete
        </button>
      </header>

      {/* Drawing toolbar */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-slate-200 px-4 py-2 dark:border-slate-700">
        {/* Color swatches */}
        <div className="flex gap-1">
          {COLORS.map((c, i) => (
            <button
              key={c}
              type="button"
              onClick={() => { setEraser(false); setHand(false); setColor(c); }}
              style={{ backgroundColor: c }}
              title={COLOR_LABELS[i]}
              aria-label={COLOR_LABELS[i]}
              className={`h-5 w-5 rounded-full border-2 transition-transform hover:scale-110 ${
                color === c && !eraser && !hand
                  ? "border-slate-500 ring-2 ring-slate-200"
                  : "border-white dark:border-slate-900"
              }`}
            />
          ))}
        </div>

        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700" aria-hidden />

        {/* Pen width */}
        <div className="flex items-center gap-1">
          {WIDTHS.map((w) => (
            <button
              key={w}
              type="button"
              onClick={() => { setEraser(false); setHand(false); setPenWidth(w); }}
              title={w === 2 ? "Thin" : w === 4 ? "Medium" : "Thick"}
              className={`flex h-7 w-7 items-center justify-center rounded ${
                !eraser && !hand && penWidth === w ? "bg-slate-200 dark:bg-slate-700" : "hover:bg-slate-100 dark:hover:bg-slate-800"
              }`}
            >
              <span
                aria-hidden
                className="rounded-full"
                style={{
                  display: "block",
                  width: w * 2.5,
                  height: w * 2.5,
                  backgroundColor: eraser || hand ? "#94a3b8" : color,
                }}
              />
            </button>
          ))}
        </div>

        <div className="h-4 w-px bg-slate-200 dark:bg-slate-700" aria-hidden />

        {/* Eraser */}
        <button
          type="button"
          onClick={() => { setEraser((v) => !v); setHand(false); }}
          title="Eraser"
          className={`rounded px-2 py-1 text-xs font-medium ${
            eraser ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          🧽 Eraser
        </button>

        {/* Hand / pan tool */}
        <button
          type="button"
          onClick={() => { setHand((v) => !v); setEraser(false); }}
          title="Pan (or hold Space)"
          className={`rounded px-2 py-1 text-xs font-medium ${
            hand ? "bg-slate-800 text-white dark:bg-slate-200 dark:text-slate-900" : "text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          }`}
        >
          ✋ Hand
        </button>

        {/* Right side: zoom, reset view, undo, clear */}
        <div className="ml-auto flex items-center gap-1">
          {/* Zoom controls */}
          <button
            type="button"
            onClick={() => zoomAtCenter(scale / 1.25)}
            title="Zoom out"
            className="rounded px-2 py-1 text-sm leading-none text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            −
          </button>
          <button
            type="button"
            onClick={() => zoomAtCenter(1)}
            title="Reset zoom to 100%"
            className="w-12 rounded py-1 text-center text-xs text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            {Math.round(scale * 100)}%
          </button>
          <button
            type="button"
            onClick={() => zoomAtCenter(scale * 1.25)}
            title="Zoom in"
            className="rounded px-2 py-1 text-sm leading-none text-slate-500 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            +
          </button>

          <div className="mx-1 h-4 w-px bg-slate-200 dark:bg-slate-700" aria-hidden />

          <button
            type="button"
            onClick={handleResetView}
            title="Return to origin (reset pan and zoom)"
            className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            ⌂
          </button>
          <button
            type="button"
            onClick={handleUndo}
            disabled={strokes.length === 0}
            title="Undo last stroke"
            className="rounded px-2 py-1 text-xs font-medium text-slate-600 hover:bg-slate-100 disabled:opacity-30 dark:text-slate-300 dark:hover:bg-slate-800"
          >
            ↩ Undo
          </button>
          <button
            type="button"
            onClick={handleClear}
            disabled={strokes.length === 0}
            title="Clear all strokes"
            className="rounded px-2 py-1 text-xs font-medium text-rose-600 hover:bg-rose-50 disabled:opacity-30 dark:text-rose-400 dark:hover:bg-rose-950"
          >
            Clear
          </button>
        </div>
      </div>

      {/* Drawing surface */}
      <div className="flex-1 overflow-hidden bg-white">
        <canvas
          ref={canvasRef}
          className="h-full w-full"
          style={{ touchAction: "none" }}
        />
      </div>
    </section>
  );
}
